import { useTranscriptionNotesMutation } from '@modules/transcriptions/useTranscriptionNotesMutation';
import { useTranscriptionsState } from '@modules/transcriptions/useTranscriptionsState';
import { useCallback } from 'react';
import { useAuthContext } from 'src/auth/useAuthContext';
import { useActionBarState } from 'src/features/ActionsBar/useActionBarState';
import { AudioType, useAudio } from 'src/features/audio';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';

export const useTranscriptionControl = () => {
  const audio = useAudio();
  const { user } = useAuthContext();

  const { activeVoiceChannel, setActiveVoiceChannel } = useLivekitRTCState();

  const {
    activeTranscriptionChannelId,
    activeTranscriptionNote,
    setActiveTranscriptionChannelId,
    setActiveTranscriptionOwner,
    setActiveTranscriptionNote,
    setNote,
  } = useTranscriptionsState();

  const { setOpenAction } = useActionBarState();

  const { startNotes, stopNotes } = useTranscriptionNotesMutation();

  const handleStartTranscription = useCallback(
    async (audioOnly = true) => {
      audio.play(AudioType.NOTE_TAKING_START);

      const response = await startNotes({
        channelId: activeVoiceChannel.channel._id,
        audioOnly,
      });
      setNote(response.document);
      setActiveTranscriptionNote(response.document);
      setOpenAction('notes');
      setActiveTranscriptionChannelId(activeVoiceChannel.channel._id);
      setActiveTranscriptionOwner(user?._id);
    },
    [
      startNotes,
      activeVoiceChannel?.channel?._id,
      setNote,
      setActiveTranscriptionNote,
      setOpenAction,
      setActiveTranscriptionChannelId,
      setActiveTranscriptionOwner,
      user?._id,
      audio,
    ],
  );

  const handleStopTranscription = useCallback(async () => {
    audio.play(AudioType.NOTE_TAKING_END);

    await stopNotes({ channelId: activeVoiceChannel.channel._id });
    if (activeTranscriptionNote) {
      setActiveTranscriptionNote({
        ...activeTranscriptionNote,
        loadingTranscription: true,
      });
    }

    setActiveTranscriptionOwner(undefined);

    setActiveVoiceChannel({
      ...activeVoiceChannel,
      channel: {
        ...activeVoiceChannel.channel,
        egressId: undefined,
      },
    });
  }, [
    activeTranscriptionNote,
    setActiveVoiceChannel,
    activeVoiceChannel,
    stopNotes,
    setActiveTranscriptionNote,
    setActiveTranscriptionOwner,
    audio,
  ]);

  const handleToggleTranscription = async (audioOnly = true) => {
    if (!activeVoiceChannel?.channel?._id) return;

    // Start Transcription
    if (!activeTranscriptionChannelId) {
      await handleStartTranscription(audioOnly);
      return;
    }

    // Stop Transcription
    await handleStopTranscription();
  };

  return {
    handleStartTranscription,
    handleStopTranscription,
    handleToggleTranscription,
  };
};
